import type { StreakStatus, StreakDetails, WeeklyGoal } from '~/types/streak.types';
import { StreakEvents } from '~/enums/tracking/tracking-events.enums';

export const STREAK_FEATURE_OFF = Symbol('streak-feature-off');
export type StreakStatusResult = StreakStatus | typeof STREAK_FEATURE_OFF;

function isNotFound (error: unknown): boolean {
  return (error as { statusCode?: number })?.statusCode === 404;
}

function unwrap<T> (res: { data: T } | null | undefined, endpoint: string): T {
  if (!res?.data) {
    throw new Error(`Streak API returned no data envelope (${endpoint})`);
  }
  return res.data;
}

// a 404 on /streak/status is indistinguishable from a route/proxy outage,
// so leave a trace in both analytics and error monitoring
function reportFeatureOff (): void {
  try {
    useNuxtApp().$sentry?.captureMessage('Streak status returned 404 — feature off or route broken', 'warning');
    useTracking().dispatchTrackingEvent(StreakEvents.FeatureUnavailable404);
  } catch (error) {
    console.error('Failed to report streak feature-off detection', error);
  }
}

export function useStreaks () {
  const getStatus = async (): Promise<StreakStatusResult> => {
    try {
      const res = await useAuthenticatedFetchSimplified<{ data: StreakStatus }>(
        useApiUrl('/streak/status'),
        { method: 'GET' },
      );
      return unwrap(res, '/streak/status');
    } catch (error) {
      if (isNotFound(error)) {
        reportFeatureOff();
        return STREAK_FEATURE_OFF;
      }
      throw error;
    }
  };

  const getDetails = async (): Promise<StreakDetails> => {
    const res = await useAuthenticatedFetchSimplified<{ data: StreakDetails }>(
      useApiUrl('/streak'),
      { method: 'GET' },
    );
    return unwrap(res, '/streak');
  };

  const updateGoal = async (weeklyGoal: WeeklyGoal): Promise<StreakDetails> => {
    const res = await useAuthenticatedFetchSimplified<{ data: StreakDetails }>(
      useApiUrl('/streak/settings'),
      { method: 'PATCH', body: { weeklyGoal }},
    );
    return unwrap(res, '/streak/settings');
  };

  return { getStatus, getDetails, updateGoal };
}
